import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SectionHeading from "@/components/base/SectionHeading";
import { getMovieById, movies } from "@/mocks/movies";
import { getCategoryMeta } from "@/mocks/categories";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import DetailHero from "./components/DetailHero";
import ShareModal from "./components/ShareModal";
import RelatedMovies from "./components/RelatedMovies";

export default function DetailPage() {
  const { id = "" } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [justFavorited, setJustFavorited] = useState(false);

  const typeLabel = movie ? getCategoryMeta(movie.type)?.label ?? "影视" : "影视";

  usePageMeta({
    title: movie
      ? `${movie.title} - ${movie.genres[0] ?? typeLabel} - 棒棒TV影视详情`
      : "影视详情 - 棒棒TV",
    description: movie
      ? `${movie.title}：${movie.synopsis}导演 ${movie.director}，主演 ${movie.cast.join("、")}。`
      : "棒棒TV影视详情页，提供导演、主演、上映日期与剧情介绍等完整信息。",
    keywords: movie
      ? `${movie.title},${movie.genres.join(",")},${typeLabel},棒棒TV,影视详情`
      : "棒棒TV,影视详情",
  });

  const related = useMemo(() => {
    if (!movie) return [];
    const sameType = movies.filter((item) => item.id !== movie.id && item.type === movie.type);
    const scored = sameType
      .map((item) => ({
        item,
        score: item.genres.filter((genre) => movie.genres.includes(genre)).length,
      }))
      .sort((a, b) => b.score - a.score);
    return scored.slice(0, 4).map((entry) => entry.item);
  }, [movie]);

  const handleToggleFavorite = () => {
    if (!movie) return;
    const wasFavorite = isFavorite(movie.id);
    toggleFavorite(movie.id);
    if (!wasFavorite) {
      setJustFavorited(true);
      setTimeout(() => setJustFavorited(false), 2200);
    }
  };

  if (!movie) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="w-full max-w-md rounded-lg border border-dashed border-background-300 py-16 text-center">
            <i className="ri-file-warning-line text-4xl text-foreground-300"></i>
            <h1 className="mt-4 text-lg font-bold text-foreground-800">影视详情暂无数据</h1>
            <p className="mt-2 text-sm text-foreground-500">
              该影视详情加载失败，请刷新重试，或返回首页浏览其他内容。
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-home-4-line"></i>
                返回首页
              </Link>
              <Link
                to="/special"
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:border-accent-400"
              >
                浏览专题
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 pb-6">
        <DetailHero
          movie={movie}
          isFavorite={isFavorite(movie.id)}
          onToggleFavorite={handleToggleFavorite}
          onShare={() => setShareOpen(true)}
          isNewFavorite={justFavorited}
        />

        <div className="mx-auto max-w-[1180px] px-4 py-8 md:px-8 md:py-10">
          <nav className="mb-6 flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-accent-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${movie.type}`} className="transition-colors hover:text-accent-600">
              {typeLabel}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{movie.title}</span>
          </nav>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div className="min-w-0 space-y-8">
              <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <SectionHeading title="剧情介绍" icon="ri-file-text-line" />
                <p className="text-sm leading-7 text-foreground-700">{movie.story}</p>
              </section>

              <section>
                <SectionHeading
                  title="相关推荐"
                  icon="ri-movie-line"
                  moreLabel={`更多${typeLabel}`}
                  moreLink={`/category/${movie.type}`}
                />
                <RelatedMovies movies={related} />
              </section>
            </div>

            <aside className="min-w-0">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="text-base font-bold text-foreground-950">影视信息</h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground-500">导演</dt>
                    <dd className="text-foreground-800">{movie.director}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground-500">主演</dt>
                    <dd className="text-foreground-800">{movie.cast.join("、")}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground-500">上映日期</dt>
                    <dd className="text-foreground-800">{movie.releaseDate}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground-500">类型</dt>
                    <dd className="text-foreground-800">{movie.genres.join(" · ")}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground-500">地区</dt>
                    <dd className="text-foreground-800">{movie.region}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground-500">年份</dt>
                    <dd className="text-foreground-800">{movie.year}</dd>
                  </div>
                </dl>

                <Link
                  to={`/category/${movie.type}`}
                  className="mt-5 flex items-center justify-between rounded-md bg-primary-50 px-4 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                >
                  所属分类：{typeLabel}频道
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              </div>

              <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-5">
                <h3 className="flex items-center gap-2 text-sm font-bold text-accent-800">
                  <i className="ri-lightbulb-line"></i>
                  观影小贴士
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  点击右上角收藏按钮，可以把想看的内容保存到本地收藏清单，方便下次快速找到。
                </p>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} movie={movie} />
    </div>
  );
}